import { NgModule } from '@angular/core'
import { RouterModule, Routes } from '@angular/router'
import { LoginComponent } from './features/login/login.component'
import { Error401Component } from './features/error-401/error-401.component'
import { Error404Component } from './features/error-404/error-404.component'
import { SignedComponent } from './features/signed/signed.component'
import { LayoutComponent } from './core/layout/layout.component'
import { AuthGuard } from './core/auth/guards/auth.guard'
import { LoginGuard } from './core/auth/guards/login.guard'
import { AdminGuard } from './features/admin/guards/admin.guard'
import { DepartmentsResolver } from './core/resolvers/departments.resolver'

/**
 * App Routing Module - Angular Router configuration
 * Migrated from UI-Router (@uirouter/angular)
 *
 * Architecture based on reference project: ca-assets-frontend-ng14
 *
 * Structure:
 * - /login - Standalone route (no guard, no layout)
 * - / - LayoutComponent wrapper (with AuthGuard)
 *   - Contains header, loading bar, and nested router-outlet
 *   - All authenticated routes are children of LayoutComponent
 *   - DepartmentsResolver runs once at layout level
 * - /401, /404 - Error pages (no layout)
 *
 * Layout Pattern Benefits:
 * - Header only shows for authenticated routes (inside LayoutComponent)
 * - AuthGuard applied once at layout level (not per route)
 * - Clean separation between authenticated and public layouts
 * - Standard Angular pattern (no named views needed)
 *
 * Notes:
 * - All routes fully migrated to Angular Router (UI-Router removed)
 * - HTML5 mode (pushState) - server must serve index.html for all routes
 */
const routes: Routes = [
    // Login - Standalone route with LoginGuard (redirects authenticated users to landingPage)
    {
        path: 'login',
        component: LoginComponent,
        canActivate: [LoginGuard]
    },

    // Authenticated routes - wrapped in LayoutComponent
    {
        path: '',
        component: LayoutComponent,
        canActivate: [AuthGuard],
        resolve: {
            departments: DepartmentsResolver
        },
        children: [
            // Default redirect to landing page
            {
                path: '',
                redirectTo: 'landingPage',
                pathMatch: 'full'
            },

            // Landing Page - Lazy loaded
            {
                // No breadcrumb: the landing page is home (represented by the home icon),
                // it must never appear as a crumb. NavigationEnd in app.module clears the
                // trail on /landingPage anyway — keeping a breadcrumb here only resurfaces
                // it when that guard is bypassed by a stray fragment/query (DEV-796).
                path: 'landingPage',
                loadChildren: () =>
                    import('./features/landing-page/landing-page.module')
                        .then(m => m.LandingPageModule)
            },

            // Change Password - Lazy loaded
            {
                path: 'changePassword',
                loadChildren: () =>
                    import('./features/change-password/change-password.module')
                        .then(m => m.ChangePasswordModule),
                data: { breadcrumb: 'Změna hesla' }
            },

            // Statistics - Lazy loaded
            {
                path: 'statistics',
                loadChildren: () =>
                    import('./features/statistics/statistics.module')
                        .then(m => m.StatisticsModule),
                data: { breadcrumb: 'Statistiky' }
            },

            // Taxes - Lazy loaded (requires 'taxes' permission)
            {
                path: 'taxes',
                loadChildren: () =>
                    import('./features/taxes/taxes.module')
                        .then(m => m.TaxesModule),
                data: { breadcrumb: 'Daně' }
            },

            // Purchase Case - Lazy loaded (requires 'purchase' permission)
            {
                path: 'purchase',
                loadChildren: () =>
                    import('./features/purchase-case/purchase-case.module')
                        .then(m => m.PurchaseCaseModule),
                data: { breadcrumb: 'Nákupy půdy' }
            },

            // group - Lazy loaded
            {
                path: 'group',
                loadChildren: () =>
                    import('./features/group/group.module')
                        .then(m => m.GroupModule),
                data: { breadcrumb: 'Skupiny' }
            },

            // payments - Lazy loaded (requires 'payments' permission)
            {
                path: 'payments',
                loadChildren: () =>
                    import('./features/payments/payments.module')
                        .then(m => m.PaymentsModule),
                data: { breadcrumb: 'Platby' }
            },

            // subjects - Lazy loaded
            {
                path: 'subjects',
                loadChildren: () =>
                    import('./features/subjects/subjects.module')
                        .then(m => m.SubjectsModule),
                data: { breadcrumb: 'Osoby' }
            },

            // contracts - Lazy loaded
            {
                path: 'contracts',
                loadChildren: () =>
                    import('./features/contracts/contracts.module')
                        .then(m => m.ContractsModule),
                data: { breadcrumb: 'Pachtovní smlouvy' }
            },

            // subcontract - Lazy loaded (requires 'subcontract' permission)
            {
                path: 'subcontract',
                loadChildren: () =>
                    import('./features/contracts/subcontract/subcontract.module')
                        .then(m => m.SubcontractModule),
                data: { breadcrumb: 'Podpachtovní smlouvy' }
            },

            // parcels - Lazy loaded
            {
                path: 'parcels',
                loadChildren: () =>
                    import('./features/parcels/parcels.module')
                        .then(m => m.ParcelsModule),
                data: { breadcrumb: 'Parcely' }
            },

            // barter - Lazy loaded
            {
                path: 'barter',
                loadChildren: () =>
                    import('./features/barter/barter.module')
                        .then(m => m.BarterModule),
                data: { breadcrumb: 'Směny užívání' }
            },

            // template - Lazy loaded
            {
                path: 'template',
                loadChildren: () =>
                    import('./features/template/template.module')
                        .then(m => m.TemplateModule),
                data: { breadcrumb: 'Šablony a výchozí údaje' }
            },

            // buildings - Lazy loaded
            {
                path: 'buildings',
                loadChildren: () =>
                    import('./features/buildings/buildings.module')
                        .then(m => m.BuildingsModule),
                data: { breadcrumb: 'Budovy' }
            },

            // bulkOperation - Lazy loaded
            {
                path: 'bulkOperation',
                loadChildren: () =>
                    import('./features/bulk-operation/bulk-operation.module')
                        .then(m => m.BulkOperationModule),
                data: { breadcrumb: 'Hromadné operace' }
            },

            // nonCadastral - Lazy loaded
            {
                path: 'nonCadastral',
                loadChildren: () =>
                    import('./features/non-cadastral/non-cadastral.module')
                        .then(m => m.NonCadastralModule),
                data: { breadcrumb: 'Smlouvy mimo data katastru' }
            },

            // map - Lazy loaded
            {
                path: 'map',
                loadChildren: () =>
                    import('./features/map/map.module')
                        .then(m => m.MapModule),
                data: { breadcrumb: 'Mapa' }
            },

            // cadastralData - Lazy loaded
            {
                path: 'cadastralData',
                loadChildren: () =>
                    import('./features/cadastral-data/cadastral-data.module')
                        .then(m => m.CadastralDataModule),
                data: { breadcrumb: 'Data katastru nemovitostí' }
            },

            // admin - Lazy loaded (requires 'admin' permission)
            {
                path: 'admin',
                canActivate: [AdminGuard],
                loadChildren: () =>
                    import('./features/admin/admin.module')
                        .then(m => m.AdminModule),
                data: { breadcrumb: 'Administrace' }
            }
        ]
    },

    // AZH - Password reset page (standalone, no layout)
    {
        path: 'azh',
        loadChildren: () =>
            import('./features/azh/azh.module')
                .then(m => m.AzhModule)
    },

    // AZU - User activation page (standalone, no layout)
    {
        path: 'azu',
        loadChildren: () =>
            import('./features/azu/azu.module')
                .then(m => m.AzuModule)
    },

    // Error Pages (standalone, no layout)
    {
        path: '401',
        component: Error401Component
    },
    {
        path: '404',
        component: Error404Component
    },

    // SIGNI post-signature landing page (standalone, no layout, no auth)
    // SIGNI redirects users here after signing a contract document.
    {
        path: 'signed',
        component: SignedComponent
    },

    // Catch-all route - redirect unknown paths to 404
    // (UI-Router was fully removed, Angular Router now handles all routes)
    {
        path: '**',
        redirectTo: '/404'
    }
]

@NgModule({
    imports: [RouterModule.forRoot(routes, {
        // useHash: false (default) - HTML5 mode (pushState) - modernized routing
        // Server must serve index.html for all routes
        // enableTracing: true  // Uncomment for debugging route changes
    })],
    exports: [RouterModule]
})
export class AppRoutingModule {}
